<script setup>
// NOTICE: KAPTION-INTEGRATION
// sales-pipeline.COLUMN.1 — a stage column: colour bar, name, count, WIP "n/limit"
// (red when exceeded), drag-target highlight (dashed red when a drop would break
// the WIP limit). Terminal (Won/Lost) columns collapse to a ~48px strip that
// expands on hover and stays open when clicked.

import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import KaptionSalesPipelineDropList from './KaptionSalesPipelineDropList.vue';
import { wipState } from 'dashboard/helper/kaptionSalesPipeline.js';

const props = defineProps({
  stage: { type: Object, required: true },
  stages: { type: Array, default: () => [] },
  items: { type: Array, default: () => [] },
  total: { type: Number, default: 0 },
  loading: { type: Boolean, default: false },
  hasMore: { type: Boolean, default: false },
  collapsed: { type: Boolean, default: false },
  dragOver: { type: Boolean, default: false },
  dragExceeds: { type: Boolean, default: false },
  moveGuard: { type: Function, default: () => true },
  nowSeconds: { type: Number, default: 0 },
  focusedId: { type: [Number, String], default: null },
  condensed: { type: Boolean, default: false },
});

const emit = defineEmits([
  'open',
  'drop',
  'loadMore',
  'toggleCollapse',
  'dragStart',
  'dragEnd',
]);

const { t } = useI18n();
const hoverOpen = ref(false);

const isStrip = computed(() => props.collapsed && !hoverOpen.value);
const wip = computed(() => wipState(props.total, props.stage.wipLimit));

const terminalIcon = computed(() => {
  if (props.stage.terminal === 'won') return 'i-lucide-trophy';
  if (props.stage.terminal === 'lost') return 'i-lucide-circle-x';
  return '';
});

function onScroll(event) {
  const el = event.target;
  if (
    props.hasMore &&
    !props.loading &&
    el.scrollHeight - (el.scrollTop + el.clientHeight) < 120
  ) {
    emit('loadMore', props.stage.id);
  }
}
</script>

<template>
  <section
    class="pipeline-column relative flex flex-col min-h-0 rounded-xl border bg-n-alpha-1 transition-[width,flex-basis] duration-200"
    :class="[
      isStrip
        ? 'is-strip w-10 shrink-0 cursor-pointer'
        : 'flex-1 min-w-[14rem]',
      collapsed && !isStrip ? 'shrink-0 w-[17rem] shadow-lg z-10' : '',
      dragOver && dragExceeds
        ? 'border-dashed border-2 border-n-ruby-9'
        : dragOver
          ? 'border-n-teal-8 ring-1 ring-n-teal-8 bg-n-teal-2'
          : 'border-n-weak',
    ]"
    :data-stage-id="stage.id"
    :data-terminal="stage.terminal || undefined"
    :data-collapsed="isStrip ? 'true' : 'false'"
    data-testid="pipeline-column"
    @mouseenter="collapsed && (hoverOpen = true)"
    @mouseleave="hoverOpen = false"
    @click="isStrip && emit('toggleCollapse', stage.id)"
  >
    <div
      class="h-0.5 rounded-t-xl shrink-0"
      :style="{ backgroundColor: stage.color }"
    />

    <!-- Collapsed strip -->
    <div
      v-if="isStrip"
      class="flex flex-col items-center gap-1.5 py-1.5 flex-1 min-h-0"
      :title="t('KAPTION_PLATFORM.SALES_PIPELINE.COLUMN.EXPAND')"
    >
      <i
        v-if="terminalIcon"
        :class="terminalIcon"
        class="size-4"
        :style="{ color: stage.color }"
      />
      <span
        class="text-[10px] font-semibold px-1 rounded-full bg-n-alpha-2 text-n-slate-11"
      >
        {{ total }}
      </span>
      <span
        class="pipeline-strip-name text-xs font-semibold text-n-slate-12 truncate"
      >
        {{ stage.name }}
      </span>
    </div>

    <template v-else>
      <!-- Hover already expanded a collapsed column, so the click that pins it
           lands here (a mouse can never click the strip itself). -->
      <header
        class="flex items-center justify-between gap-2 px-2.5 py-1.5"
        :class="collapsed ? 'cursor-pointer' : ''"
        :title="
          collapsed ? t('KAPTION_PLATFORM.SALES_PIPELINE.COLUMN.PIN_OPEN') : ''
        "
        data-testid="pipeline-column-header"
        @click="collapsed && emit('toggleCollapse', stage.id)"
      >
        <div class="flex items-center gap-1.5 min-w-0">
          <i
            v-if="terminalIcon"
            :class="terminalIcon"
            class="size-4 shrink-0"
            :style="{ color: stage.color }"
          />
          <span
            v-else
            class="size-2 rounded-full shrink-0"
            :style="{ backgroundColor: stage.color }"
          />
          <span class="text-xs font-semibold truncate text-n-slate-12">
            {{ stage.name }}
          </span>
        </div>
        <div class="flex items-center gap-1 shrink-0">
          <span
            v-if="stage.wipLimit"
            class="text-[11px] font-medium px-1.5 py-0.5 rounded-full"
            :class="
              wip === 'exceeded'
                ? 'bg-n-ruby-3 text-n-ruby-11'
                : wip === 'full'
                  ? 'bg-n-amber-3 text-n-amber-11'
                  : 'bg-n-alpha-2 text-n-slate-11'
            "
            :title="t('KAPTION_PLATFORM.SALES_PIPELINE.COLUMN.WIP_LIMIT')"
            data-testid="pipeline-column-wip"
          >
            {{ total }}/{{ stage.wipLimit }}
          </span>
          <span
            v-else
            class="text-[11px] font-medium px-1.5 py-0.5 rounded-full bg-n-alpha-2 text-n-slate-11"
            data-testid="pipeline-column-count"
          >
            {{ total }}
          </span>
          <button
            v-if="stage.terminal"
            type="button"
            class="p-0.5 rounded text-n-slate-10 hover:text-n-slate-12 hover:bg-n-alpha-2"
            :title="
              collapsed
                ? t('KAPTION_PLATFORM.SALES_PIPELINE.COLUMN.PIN_OPEN')
                : t('KAPTION_PLATFORM.SALES_PIPELINE.COLUMN.COLLAPSE')
            "
            data-testid="pipeline-column-collapse"
            @click.stop="emit('toggleCollapse', stage.id)"
          >
            <i
              :class="collapsed ? 'i-lucide-pin' : 'i-lucide-chevrons-right'"
              class="size-3.5 block"
            />
          </button>
        </div>
      </header>
    </template>

    <div
      v-show="!isStrip"
      class="flex-1 min-h-0 overflow-y-auto px-1.5 pb-1.5"
      @scroll="onScroll"
    >
      <KaptionSalesPipelineDropList
        :items="items"
        :stage="stage"
        :stages="stages"
        :move-guard="moveGuard"
        :now-seconds="nowSeconds"
        :focused-id="focusedId"
        :condensed="condensed"
        min-height-class="min-h-[6rem]"
        @drop="emit('drop', $event)"
        @open="emit('open', $event)"
        @drag-start="emit('dragStart', $event)"
        @drag-end="emit('dragEnd', $event)"
      />
      <div v-if="loading" class="flex justify-center py-3">
        <Spinner class="text-n-brand" />
      </div>
      <p
        v-else-if="!items.length"
        class="py-6 text-center text-xs text-n-slate-10 pointer-events-none"
      >
        {{ t('KAPTION_PLATFORM.SALES_KANBAN.NO_CONVERSATIONS') }}
      </p>
    </div>
  </section>
</template>

<style scoped>
.pipeline-strip-name {
  writing-mode: vertical-rl;
  max-height: 12rem;
}
</style>
